/**
 * BulkTaskConfirmModal
 * Modale di conferma per creazione task bulk dai suggerimenti agent.
 * All'apertura chiama l'AI per generare titoli e descrizioni strutturate.
 * L'utente può modificare tutto prima di confermare.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { X, ListTodo, Loader2, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Sparkles, RotateCcw, ImageIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { AgentRequest } from '@/lib/types/whatsappAgent';
import { WhatsAppImagePicker } from './WhatsAppImagePicker';
import { tip } from '@/components/messages/ChatTooltip';
import { useOrgAiEnabled, isOrgAiEnabledNow, AI_DISABLED_TOOLTIP } from '@/lib/hooks/useOrgAiEnabled';

export interface TaskOverride {
  title: string;
  description: string;
}

interface BulkTaskConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: AgentRequest[];
  projectName: string;
  chatId: string;
  onConfirm: (overrides: Record<string, TaskOverride>, attachments: Record<string, File[]>) => Promise<{ created: number; errors: string[] }>;
}

function getTypeIcon(type: string): string {
  switch (type) {
    case 'bug_report': return '🐛';
    case 'feature_request': return '✨';
    case 'support_request': return '🆘';
    case 'task_request': return '📋';
    case 'status_inquiry': return '❓';
    default: return '💬';
  }
}

export function BulkTaskConfirmModal({
  isOpen,
  onClose,
  suggestions,
  projectName,
  chatId,
  onConfirm,
}: BulkTaskConfirmModalProps) {
  const [overrides, setOverrides] = useState<Record<string, TaskOverride>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<{ created: number; errors: string[] } | null>(null);
  const [attachmentsMap, setAttachmentsMap] = useState<Record<string, File[]>>({});
  const [showImagePickerFor, setShowImagePickerFor] = useState<string | null>(null);
  const hasGenerated = useRef(false);
  // Interruttore IA dell'azienda: spento = testi originali dei suggerimenti, nessuna chiamata IA.
  const aiEnabled = useOrgAiEnabled();
  // Ref stabile per suggestions (evita re-trigger dell'effect)
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;

  // Genera testi con AI
  const generateTexts = useCallback(async () => {
    const currentSuggestions = suggestionsRef.current;
    if (!isOrgAiEnabledNow()) {
      const original: Record<string, TaskOverride> = {};
      for (const s of currentSuggestions) {
        original[s.id] = { title: s.title, description: s.description };
      }
      setOverrides(original);
      return;
    }
    setGenerating(true);
    try {
      // Payload compatto: solo campi necessari per l'AI, original_text troncato
      const payload = currentSuggestions.map(s => ({
        id: s.id,
        title: s.title,
        original_text: (s.original_text || '').substring(0, 200),
        request_type: s.request_type,
      }));

      const { data, error } = await supabase.functions.invoke('generate-task-texts', {
        body: { suggestions: payload },
      });

      if (error) throw error;

      const tasks: Array<{ id: string; title: string; description: string }> = data?.tasks || [];
      const newOverrides: Record<string, TaskOverride> = {};
      for (const task of tasks) {
        newOverrides[task.id] = { title: task.title, description: task.description };
      }
      // Fallback per suggerimenti non trovati nella risposta AI
      for (const s of currentSuggestions) {
        if (!newOverrides[s.id]) {
          newOverrides[s.id] = { title: s.title, description: s.description };
        }
      }
      setOverrides(newOverrides);
    } catch (err) {
      console.error('[BulkTaskConfirmModal] AI generation error:', err);
      // Fallback: usa i testi originali dei suggerimenti
      const fallback: Record<string, TaskOverride> = {};
      for (const s of currentSuggestions) {
        fallback[s.id] = { title: s.title, description: s.description };
      }
      setOverrides(fallback);
    } finally {
      setGenerating(false);
    }
  }, []);

  // Chiama AI UNA SOLA VOLTA quando il modale si apre
  useEffect(() => {
    if (isOpen && !hasGenerated.current) {
      hasGenerated.current = true;
      setResult(null);
      generateTexts();
    }
    if (!isOpen) {
      hasGenerated.current = false;
    }
  }, [isOpen, generateTexts]);

  const handleUpdateTitle = useCallback((id: string, title: string) => {
    setOverrides(prev => ({
      ...prev,
      [id]: { ...prev[id], title }
    }));
  }, []);

  const handleUpdateDescription = useCallback((id: string, description: string) => {
    setOverrides(prev => ({
      ...prev,
      [id]: { ...prev[id], description }
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    setCreating(true);
    try {
      const res = await onConfirm(overrides, attachmentsMap);
      setResult(res);
      if (res.errors.length === 0) {
        setTimeout(() => onClose(), 1200);
      }
    } catch {
      setResult({ created: 0, errors: ['Errore imprevisto'] });
    } finally {
      setCreating(false);
    }
  }, [overrides, attachmentsMap, onConfirm, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={!generating && !creating ? onClose : undefined} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <ListTodo className="w-5 h-5 text-blue-600" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-[#37352F] dark:text-white">
              Crea {suggestions.length} task
            </h2>
            <p className="text-xs text-[#787774] mt-0.5">
              Progetto: <span className="font-medium text-[#37352F] dark:text-white/80">{projectName}</span>
            </p>
          </div>
          {!generating && (
            <button
              onClick={generateTexts}
              disabled={creating || !aiEnabled}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors disabled:opacity-50"
              {...tip(aiEnabled ? 'Rigenera testi con AI' : AI_DISABLED_TOOLTIP)}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Rigenera
            </button>
          )}
          <button
            onClick={onClose}
            disabled={generating || creating}
            className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-[#787774]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {generating ? (
            /* Loading state: AI generating */
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="relative">
                <Sparkles className="w-8 h-8 text-purple-500 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[#37352F] dark:text-white">
                  Generazione testi con AI...
                </p>
                <p className="text-xs text-[#787774] mt-1">
                  Sto creando titoli e descrizioni strutturate per {suggestions.length} task
                </p>
              </div>
            </div>
          ) : (
            /* Task list */
            <div className="space-y-3">
              {suggestions.map((suggestion, index) => {
                const isExpanded = expandedId === suggestion.id;
                const override = overrides[suggestion.id];

                return (
                  <div
                    key={suggestion.id}
                    className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-[#FBFBFA] dark:bg-gray-800/50"
                  >
                    {/* Compact row */}
                    <div className="flex items-start gap-3 px-4 py-3">
                      <span className="text-sm mt-0.5 flex-shrink-0">{getTypeIcon(suggestion.request_type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] text-[#787774] font-medium">#{index + 1}</span>
                          {suggestion.sender_name && (
                            <span className="text-[10px] text-[#787774]">da {suggestion.sender_name}</span>
                          )}
                        </div>
                        <input
                          type="text"
                          value={override?.title || ''}
                          onChange={(e) => handleUpdateTitle(suggestion.id, e.target.value)}
                          className="w-full text-sm font-medium text-[#37352F] dark:text-white bg-transparent border-none outline-none focus:ring-0 p-0 placeholder:text-[#787774]"
                          placeholder="Titolo task..."
                        />
                      </div>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : suggestion.id)}
                        className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 flex-shrink-0 mt-0.5"
                        {...tip(isExpanded ? 'Comprimi' : 'Espandi per modificare descrizione')}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-[#787774]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#787774]" />
                        )}
                      </button>
                    </div>

                    {/* Expanded: description editor + image attachments */}
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0">
                        <textarea
                          value={override?.description || ''}
                          onChange={(e) => handleUpdateDescription(suggestion.id, e.target.value)}
                          rows={4}
                          className="w-full text-xs text-[#37352F] dark:text-white/90 bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-blue-400 dark:focus:border-blue-500 resize-vertical placeholder:text-[#787774]"
                          placeholder="Descrizione task..."
                        />
                        {suggestion.original_text && (
                          <div className="mt-2 pl-3 border-l-2 border-[rgba(55,53,47,0.12)] dark:border-white/15">
                            <p className="text-[10px] text-[#787774] font-medium uppercase tracking-wide mb-0.5">Messaggio originale</p>
                            <p className="text-[11px] text-[#787774] leading-relaxed line-clamp-3">
                              {suggestion.original_text}
                            </p>
                          </div>
                        )}

                        {/* Image attachments */}
                        <div className="mt-3">
                          {attachmentsMap[suggestion.id]?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {attachmentsMap[suggestion.id].map((file, fi) => (
                                <div
                                  key={fi}
                                  className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-[10px] text-blue-700 dark:text-blue-300"
                                >
                                  <ImageIcon className="w-2.5 h-2.5" />
                                  <span className="max-w-[80px] truncate">{file.name}</span>
                                  <button
                                    onClick={() => setAttachmentsMap(prev => ({
                                      ...prev,
                                      [suggestion.id]: prev[suggestion.id].filter((_, i) => i !== fi)
                                    }))}
                                    className="ml-0.5 hover:text-red-500"
                                  >
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {showImagePickerFor === suggestion.id ? (
                            <WhatsAppImagePicker
                              chatId={chatId}
                              onSelect={(files) => {
                                setAttachmentsMap(prev => ({
                                  ...prev,
                                  [suggestion.id]: [...(prev[suggestion.id] || []), ...files]
                                }));
                                setShowImagePickerFor(null);
                              }}
                              onClose={() => setShowImagePickerFor(null)}
                            />
                          ) : (
                            <button
                              onClick={() => setShowImagePickerFor(suggestion.id)}
                              className="flex items-center gap-1.5 text-[11px] text-[#787774] hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            >
                              <ImageIcon className="w-3 h-3" />
                              {attachmentsMap[suggestion.id]?.length > 0
                                ? `${attachmentsMap[suggestion.id].length} immagin${attachmentsMap[suggestion.id].length === 1 ? 'e' : 'i'} · Aggiungi altre`
                                : 'Allega immagini dalla chat'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-[#F7F7F5] dark:bg-gray-900">
          {result && (
            <div className={`flex items-center gap-2 mb-3 text-sm ${result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {result.errors.length > 0 ? (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              ) : (
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
              )}
              <span>
                {result.created} task creat{result.created === 1 ? 'a' : 'e'}
                {result.errors.length > 0 && ` · ${result.errors.length} error${result.errors.length === 1 ? 'e' : 'i'}`}
              </span>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={creating || generating}
              className="px-4 py-2 text-sm text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={handleConfirm}
              disabled={creating || generating || !!result}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <ListTodo className="w-4 h-4" />
                  Conferma e crea {suggestions.length} task
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
